import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  request,
  type ContactListResponse,
  type ContactDetailResponse,
  type ContactSummaryResponse,
} from './client';

/**
 * Contacts — every person who has ever written to the lab, across every form.
 *
 * The rows, the record and the counts are the generated shapes, so the build
 * breaks where this screen and the API disagree. Since 036 a contact carries
 * who they are (`type`), where they are (`stage`) and a status tag per
 * record (`records`), all worked out by the API from the person's records
 * within the caller's studies; docs/api-contract-contacts.md has the rules.
 * The objects below are still hand-typed.
 */

export type ContactKind = 'participant' | 'clinician' | 'other';

/** 07 §2 person.role — who someone is to the lab. Null until a form says. */
export type PersonRole =
  | 'potential_participant' | 'family' | 'clinician' | 'student'
  | 'resident' | 'media' | 'partner' | 'other';

export const interestListLabel: Record<string, string> = {
  early_interest: 'Early interest',
  waitlist: 'Waitlist',
  clinician_protocol: 'Clinician study alerts',
  newsletter: 'Newsletter',
};

export type Contact = ContactListResponse['rows'][number];
/** One status tag: a record of theirs and where it stands. */
export type ContactRecord = Contact['records'][number];
export type ContactType = Contact['type'];
export type ContactStage = Contact['stage'];
export type TimelineEvent = ContactDetailResponse['timeline'][number];
export type ContactSummary = ContactSummaryResponse;

/** Who someone is. The API's domain/contacts.ts has the same words. */
export const contactTypeLabel: Record<ContactType, string> = {
  participant: 'Participant',
  'referred-patient': 'Referred patient',
  family: 'Family member',
  clinician: 'Clinician',
  partner: 'Partner',
  press: 'Press',
  trainee: 'Trainee',
  subscriber: 'Subscriber',
  other: 'General public',
};

/** Where someone is with the lab. */
export const contactStageLabel: Record<ContactStage, string> = {
  interested: 'Interested',
  applied: 'Applied',
  screening: 'In screening',
  enrolled: 'Enrolled',
  completed: 'Completed',
  'on-hold': 'On hold',
  'not-a-match': 'Not a match',
  withdrawn: 'Withdrawn',
  closed: 'Closed',
  asking: 'Asking',
  referring: 'Referring',
  'opted-out': 'Opted out',
  'open-request': 'Open request',
  answered: 'Answered',
  subscriber: 'Subscriber',
  unsubscribed: 'Unsubscribed',
  none: 'No activity',
};

/** The participant journey, in order — the funnel on the Contacts screen. */
export const JOURNEY: readonly ContactStage[] = ['interested', 'applied', 'screening', 'enrolled', 'completed'];

export interface Consent {
  id: string;
  purpose: 'newsletter' | 'study-alerts' | 'recontact';
  basis: 'form' | 'staff' | 'import';
  submissionId: string | null;
  note: string | null;
  grantedAt: string;
  revokedAt: string | null;
}

export interface Tag {
  id: string;
  slug: string;
  label: string;
  seeded: boolean;
  uses: number;
}

export interface ContactFilters {
  kind?: ContactKind;
  type?: ContactType;
  /** Comma-separated stages. */
  stages?: string;
  study?: string;
  openMessages?: 'true';
  audience?: 'people' | 'subscribers' | 'all';
  tag?: string;
  q?: string;
  suppressed?: 'true' | 'false';
  owner?: string;
  newsletter?: 'active' | 'unsubscribed' | 'none';
  sort?: 'recent' | 'oldest' | 'name' | 'most-active';
  page?: number;
  pageSize?: number;
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

export const useContacts = (filters: ContactFilters) =>
  useQuery({
    queryKey: ['contacts', filters],
    queryFn: () =>
      request<ContactListResponse>(`/contacts/${qs(filters as Record<string, string | number | undefined>)}`),
    placeholderData: (prev) => prev,
  });

export const useContact = (id: string | null) =>
  useQuery({
    queryKey: ['contact', id],
    queryFn: () => request<ContactDetailResponse>(`/contacts/${id}`),
    enabled: id !== null,
  });

/**
 * 07 §1's pref, access and training for one person.
 *
 * Fetched separately from the contact itself, so the drawer's first paint is
 * not held up by it. The API redacts before this arrives: `redacted` is true
 * when a field was withheld for this role, and a withheld value is '—'.
 */
export interface ContactObjects {
  redacted: boolean;
  pref: {
    channel: string;
    days: string | null;
    language: string | null;
    familyOk: string | null;
    familyFirst: string | null;
    submissionId: string;
    since: string;
  } | null;
  access: Array<{
    id: string;
    visitDate: string | null;
    language: string | null;
    mobility: string | null;
    companion: string | null;
    note: string | null;
    submissionId: string;
    createdAt: string;
  }>;
  training: Array<{
    id: string;
    route: 'rotation' | 'student' | 'fellowship';
    track: string | null;
    program: string | null;
    pgy: string | null;
    year: string | null;
    pdName: string | null;
    pdEmail: string | null;
    dates: string | null;
    onsite: string | null;
    hours: string | null;
    timing: string | null;
    ask: string | null;
    hope: string | null;
    skills: string | null;
    cv: string | null;
    submissionId: string;
    createdAt: string;
  }>;
  fits: Array<{
    submissionId: string;
    studySlug: string | null;
    kind: 'in_scope' | 'use_refer' | 'other';
    sketch: string | null;
    replyOk: 'yes' | 'no';
    createdAt: string;
  }>;
  interests: Array<{
    id: string;
    list: string;
    topics: string[];
    submissionId: string | null;
    unsubscribedAt: string | null;
    createdAt: string;
  }>;
  /** Referrals this person is on, from either side. Empty for a viewer. */
  referrals: Array<{
    submissionId: string;
    as: 'clinician' | 'patient';
    studySlug: string | null;
    ackAt: string | null;
    outreachAt: string | null;
    clinicianUpdateOk: boolean;
    createdAt: string;
  }>;
}

export const useContactObjects = (id: string | null) =>
  useQuery({
    queryKey: ['contact-objects', id],
    queryFn: () => request<ContactObjects>(`/contacts/${id}/objects`),
    enabled: id !== null,
  });

export const useContactSummary = () =>
  useQuery({ queryKey: ['contacts-summary'], queryFn: () => request<ContactSummary>('/contacts/summary') });

export const useTags = () =>
  useQuery({
    queryKey: ['tags'],
    queryFn: () => request<{ tags: Tag[] }>('/contacts/tags'),
    staleTime: 60_000,
  });

/** Everything that changes a contact invalidates the same set of views. */
function useContactWrite<V>(fn: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact'] });
      void qc.invalidateQueries({ queryKey: ['contact-objects'] });
      void qc.invalidateQueries({ queryKey: ['contacts-summary'] });
      void qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });
}

export const useSetContactTags = () =>
  useContactWrite(({ id, add, remove }: { id: string; add?: string[]; remove?: string[] }) =>
    request(`/contacts/${id}/tags`, { method: 'PATCH', body: { add: add ?? [], remove: remove ?? [] } }),
  );

export const useSetContactOwner = () =>
  useContactWrite(({ id, ownerId }: { id: string; ownerId: string | null }) =>
    request(`/contacts/${id}/owner`, { method: 'PATCH', body: { ownerId } }),
  );

/** The longest note or reason the API keeps on a stop. */
export const STOP_TEXT_MAX = 500;

/**
 * Stop contacting someone, with an optional note; or lift the stop, with a
 * reason. A stop the person asked for themselves (`suppressionSource:
 * 'person'`) is lifted only with a reason — 400 `details.reasonRequired`
 * without one — and the reason, who lifted it and when stay on the contact.
 */
export type SuppressBody = { suppressed: true; note?: string } | { suppressed: false; reason?: string };

/** The API's 400 for lifting a person's own stop — one or in bulk — without a reason. */
export const isReasonRequired = (err: unknown): boolean =>
  err instanceof ApiError &&
  err.status === 400 &&
  (err.details as { reasonRequired?: unknown } | undefined)?.reasonRequired === true;

export const useSetContactSuppressed = () =>
  useContactWrite(({ id, ...body }: SuppressBody & { id: string }) =>
    request(`/contacts/${id}/suppressed`, { method: 'PATCH', body }),
  );

/**
 * "Not a duplicate": clears the Possible duplicate flag on this contact and
 * on the one it was paired with (submissions:update). Answered once, the
 * pair is never flagged again.
 */
export const useNotDuplicate = () =>
  useContactWrite(({ id }: { id: string }) =>
    request<{ ok: boolean; cleared: string[] }>(`/contacts/${id}/not-duplicate`, { method: 'POST', body: {} }),
  );

export const useRecordConsent = () =>
  useContactWrite(({ id, purpose, note }: { id: string; purpose: Consent['purpose']; note: string }) =>
    request(`/contacts/${id}/consents`, { method: 'POST', body: { purpose, note } }),
  );

export const useRevokeConsent = () =>
  useContactWrite(({ id, purpose }: { id: string; purpose: Consent['purpose'] }) =>
    request(`/contacts/${id}/consents/${purpose}`, { method: 'DELETE' }),
  );

export type BulkAction = 'tag' | 'untag' | 'assign' | 'suppress' | 'unsuppress';

/**
 * `suppress` keeps `note` on each contact's stop; `unsuppress` is refused
 * whole without a `reason` when any selected stop is the person's own.
 */
export const useBulkContacts = () =>
  useContactWrite(
    (body: {
      action: BulkAction;
      ids: string[];
      tags?: string[];
      ownerId?: string | null;
      note?: string;
      reason?: string;
    }) => request('/contacts/bulk', { method: 'POST', body }),
  );

export const useCreateTag = () =>
  useContactWrite(({ label }: { label: string }) => request('/contacts/tags', { method: 'POST', body: { label } }));

